import axios from "axios";
import { getAuthHeader } from "./auth";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// Types
export const FeedTab = {
  FOR_YOU: "for_you",
  LOCAL: "local",
  FOLLOWING: "following",
};

export const PostType = {
  IDEA: "idea",
  ACTION_LEGISLATION: "action_legislation",
  ACTION_TOWN_HALL: "action_town_hall",
  ACTION_VOLUNTEER: "action_volunteer",
  ACTION_REFERRAL: "action_referral",
  STORY: "story",
  QUESTION: "question",
  INSIGHT: "insight",
  POLL: "poll",
  // Politician-only post types
  POLICY_CREATED: "policy_created",
  RECENT_WIN: "recent_win",
};

// ============================================================================
// POST API FUNCTIONS (Real Backend Integration)
// ============================================================================

/**
 * Create a new post
 */
export async function createPostReal(postData) {
  try {
    const headers = await getAuthHeader();
    const requestBody = {
      post_type: postData.post_type || postData.postType || "story",
      content: postData.content,
      topics: postData.topics || [],
      bill_id: postData.bill_id,
      action_type: postData.action_type,
      event_id: postData.event_id,
      parent_idea_id: postData.parent_idea_id,
      image_url: postData.image_url || postData.imageUrl,
      status: postData.status || "published",
      visibility: postData.visibility || "public",
      geo_target: postData.geo_target,
      external_link_url: postData.external_link_url,
      external_link_title: postData.external_link_title,
      location_name: postData.location_name,
      location_city: postData.location_city,
      location_state: postData.location_state,
      location_address: postData.location_address,
    };
    if (postData.anonymous !== undefined) {
      requestBody.anonymous = postData.anonymous;
    }
    if (postData.options && Array.isArray(postData.options) && postData.options.length >= 2) {
      requestBody.options = postData.options;
      requestBody.allows_multiple_choices = postData.allows_multiple_choices || false;
    }
    const response = await axios.post(
      `${API_BASE_URL}/api/posts/`,
      requestBody,
      { headers }
    );
    return response.data;
  } catch (error) {
    console.error("Error creating post:", error.response?.status, error.message);
    throw error;
  }
}

/**
 * Upload an image for a post
 */
export async function uploadPostImage(base64Data, contentType = 'image/jpeg') {
  try {
    const headers = await getAuthHeader();
    const response = await axios.post(
      `${API_BASE_URL}/api/posts/upload-image/`,
      {
        image_data: base64Data,
        content_type: contentType
      },
      { headers }
    );
    return response.data;
  } catch (error) {
    console.error("Error uploading post image:", error);
    throw error;
  }
}

/**
 * Fetch posts with filters
 */
export async function fetchPosts(filters = {}) {
  try {
    const headers = await getAuthHeader();
    const params = new URLSearchParams();
    
    if (filters.post_type) params.append("post_type", filters.post_type);
    if (filters.topic) params.append("topic", filters.topic);
    if (filters.bill_id) params.append("bill_id", filters.bill_id);
    if (filters.action_type) params.append("action_type", filters.action_type);
    if (filters.user_id) params.append("user_id", filters.user_id);
    if (filters.status) params.append("status", filters.status);
    if (filters.following_only) params.append("following_only", "true");
    if (filters.followed_posts_only) params.append("followed_posts_only", "true");
    if (filters.geo_scope) params.append("geo_scope", filters.geo_scope);
    if (filters.sort_by) params.append("sort_by", filters.sort_by);
    if (filters.page) params.append("page", filters.page);
    if (filters.page_size) params.append("page_size", filters.page_size);
    if (filters.verified_only) params.append("verified_only", "true");
    
    const response = await axios.get(
      `${API_BASE_URL}/api/posts/?${params.toString()}`,
      { headers }
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching posts:", error);
    throw error;
  }
}

/**
 * Delete a post
 */
export async function deletePost(postId) {
  try {
    const headers = await getAuthHeader();
    await axios.delete(`${API_BASE_URL}/api/posts/${postId}/`, { headers });
  } catch (error) {
    console.error("Error deleting post:", error);
    throw error;
  }
}

/**
 * Update an existing post
 */
export async function updatePost(postId, postData) {
  try {
    const headers = await getAuthHeader();
    const requestBody = {};
    
    if (postData.content !== undefined) requestBody.content = postData.content;
    if (postData.topics !== undefined) requestBody.topics = postData.topics || [];
    if (postData.image_url !== undefined) requestBody.image_url = postData.image_url || null;
    if (postData.bill_id !== undefined) requestBody.bill_id = postData.bill_id;
    if (postData.event_id !== undefined) requestBody.event_id = postData.event_id;
    if (postData.action_type !== undefined) requestBody.action_type = postData.action_type;
    if (postData.post_type !== undefined) requestBody.post_type = postData.post_type;
    if (postData.status !== undefined) requestBody.status = postData.status;
    if (postData.visibility !== undefined) requestBody.visibility = postData.visibility;
    if (postData.geo_target !== undefined) requestBody.geo_target = postData.geo_target;
    if (postData.external_link_url !== undefined) requestBody.external_link_url = postData.external_link_url || null;
    if (postData.external_link_title !== undefined) requestBody.external_link_title = postData.external_link_title || null;
    if (postData.location_name !== undefined) requestBody.location_name = postData.location_name || '';
    if (postData.location_city !== undefined) requestBody.location_city = postData.location_city || '';
    if (postData.location_state !== undefined) requestBody.location_state = postData.location_state || '';
    if (postData.location_address !== undefined) requestBody.location_address = postData.location_address || '';
    if (postData.anonymous !== undefined) requestBody.anonymous = postData.anonymous;
    if (postData.options !== undefined) {
      requestBody.options = postData.options;
      if (postData.allows_multiple_choices !== undefined) {
        requestBody.allows_multiple_choices = postData.allows_multiple_choices;
      }
    } else if (postData.allows_multiple_choices !== undefined) {
      requestBody.allows_multiple_choices = postData.allows_multiple_choices;
    }

    const response = await axios.put(
      `${API_BASE_URL}/api/posts/${postId}/`,
      requestBody,
      { headers }
    );
    return response.data;
  } catch (error) {
    console.error("Error updating post:", error);
    throw error;
  }
}

/**
 * Get a single post by ID
 */
export async function getPostDetail(postId) {
  try {
    const headers = await getAuthHeader();
    const response = await axios.get(
      `${API_BASE_URL}/api/posts/${postId}/`,
      { headers }
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching post detail:", error);
    throw error;
  }
}

/**
 * Follow a post
 */
export async function followPost(postId) {
  try {
    const headers = await getAuthHeader();
    const response = await axios.post(
      `${API_BASE_URL}/api/posts/${postId}/follow/`,
      {},
      { headers }
    );
    return response.data;
  } catch (error) {
    console.error("Error following post:", error);
    throw error;
  }
}

/**
 * Unfollow a post
 */
export async function unfollowPost(postId) {
  try {
    const headers = await getAuthHeader();
    const response = await axios.delete(
      `${API_BASE_URL}/api/posts/${postId}/follow/`,
      { headers }
    );
    return response.data;
  } catch (error) {
    console.error("Error unfollowing post:", error);
    throw error;
  }
}

/**
 * Get users following a post
 */
export async function getPostFollowers(postId) {
  try {
    const headers = await getAuthHeader();
    const response = await axios.get(
      `${API_BASE_URL}/api/posts/${postId}/followers/`,
      { headers }
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching post followers:", error);
    throw error;
  }
}

/**
 * Upvote a post
 */
export async function upvotePost(postId) {
  try {
    const headers = await getAuthHeader();
    const response = await axios.post(
      `${API_BASE_URL}/api/posts/${postId}/upvote/`,
      {},
      { headers }
    );
    return response.data;
  } catch (error) {
    console.error("Error upvoting post:", error);
    throw error;
  }
}

/**
 * Remove upvote from a post
 */
export async function removeUpvote(postId) {
  try {
    const headers = await getAuthHeader();
    const response = await axios.delete(
      `${API_BASE_URL}/api/posts/${postId}/upvote/`,
      { headers }
    );
    return response.data;
  } catch (error) {
    console.error("Error removing upvote:", error);
    throw error;
  }
}

/**
 * Cast a vote on a poll option.
 */
export async function castPollVote(postId, optionId) {
  const headers = await getAuthHeader();
  const response = await axios.post(
    `${API_BASE_URL}/api/posts/${postId}/poll/vote/`,
    { option_id: optionId },
    { headers }
  );
  return response.data;
}

/**
 * Remove a vote from a poll option.
 */
export async function removePollVote(postId, optionId) {
  const headers = await getAuthHeader();
  const response = await axios.delete(
    `${API_BASE_URL}/api/posts/${postId}/poll/vote/`,
    { headers, data: { option_id: optionId } }
  );
  return response.data;
}

/**
 * Log an action taken on a post. action_type is optional — server defaults to the post's type.
 */
export async function logPostAction(postId, { actionType, billId } = {}) {
  try {
    const headers = await getAuthHeader();
    const requestBody = {};
    if (actionType) requestBody.action_type = actionType;
    if (billId) requestBody.bill_id = billId;

    const response = await axios.post(
      `${API_BASE_URL}/api/posts/${postId}/actions/`,
      requestBody,
      { headers }
    );
    return response.data;
  } catch (error) {
    console.error("Error logging post action:", error);
    throw error;
  }
}

/**
 * Remove the viewer's logged action on a post.
 */
export async function removePostAction(postId) {
  try {
    const headers = await getAuthHeader();
    const response = await axios.delete(
      `${API_BASE_URL}/api/posts/${postId}/actions/`,
      { headers }
    );
    return response.data;
  } catch (error) {
    console.error("Error removing post action:", error);
    throw error;
  }
}

/**
 * Get actions logged for a post
 */
export async function getPostActions(postId) {
  try {
    const headers = await getAuthHeader();
    const response = await axios.get(
      `${API_BASE_URL}/api/posts/${postId}/actions/`,
      { headers }
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching post actions:", error);
    throw error;
  }
}

/**
 * Evolve an idea into a new post
 */
export async function evolveIdea(postId, evolutionData) {
  try {
    const headers = await getAuthHeader();
    const response = await axios.post(
      `${API_BASE_URL}/api/posts/${postId}/evolve/`,
      evolutionData,
      { headers }
    );
    return response.data;
  } catch (error) {
    console.error("Error evolving idea:", error);
    throw error;
  }
}

/**
 * Get evolutions of an idea
 */
export async function getIdeaEvolutions(postId) {
  try {
    const headers = await getAuthHeader();
    const response = await axios.get(
      `${API_BASE_URL}/api/posts/${postId}/evolutions/`,
      { headers }
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching idea evolutions:", error);
    throw error;
  }
}

/**
 * Accept community guidelines
 */
export async function acceptCommunityGuidelines() {
  try {
    const headers = await getAuthHeader();
    const response = await axios.post(
      `${API_BASE_URL}/api/community-guidelines/accept/`,
      {},
      { headers }
    );
    return response.data;
  } catch (error) {
    console.error("Error accepting community guidelines:", error);
    throw error;
  }
}

// ============================================================================
// COMMENTS API FUNCTIONS
// ============================================================================

/**
 * Fetch comments for a post
 */
export async function getPostComments(postId) {
  try {
    const headers = await getAuthHeader();
    const response = await axios.get(
      `${API_BASE_URL}/api/posts/${postId}/comments/`,
      { headers }
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching comments:", error);
    throw error;
  }
}

/**
 * Create a comment on a post
 */
export async function createPostComment(postId, content, parentCommentId = null, anonymous = false) {
  try {
    const headers = await getAuthHeader();
    const requestBody = { content, anonymous };
    if (parentCommentId) {
      requestBody.parent_comment_id = parentCommentId;
    }
    
    const response = await axios.post(
      `${API_BASE_URL}/api/posts/${postId}/comments/`,
      requestBody,
      { headers }
    );
    return response.data;
  } catch (error) {
    console.error("Error creating comment:", error);
    throw error;
  }
}

/**
 * Update a comment
 */
export async function updatePostComment(postId, commentId, content) {
  try {
    const headers = await getAuthHeader();
    const response = await axios.put(
      `${API_BASE_URL}/api/posts/${postId}/comments/${commentId}/`,
      { content },
      { headers }
    );
    return response.data;
  } catch (error) {
    console.error("Error updating comment:", error);
    throw error;
  }
}

/**
 * Delete a comment
 */
export async function deletePostComment(postId, commentId) {
  try {
    const headers = await getAuthHeader();
    await axios.delete(
      `${API_BASE_URL}/api/posts/${postId}/comments/${commentId}/`,
      { headers }
    );
  } catch (error) {
    console.error("Error deleting comment:", error);
    throw error;
  }
}

/**
 * Like a comment
 */
export async function likePostComment(postId, commentId) {
  try {
    const headers = await getAuthHeader();
    const response = await axios.post(
      `${API_BASE_URL}/api/posts/${postId}/comments/${commentId}/like/`,
      {},
      { headers }
    );
    return response.data;
  } catch (error) {
    console.error("Error liking comment:", error);
    throw error;
  }
}

/**
 * Unlike a comment
 */
export async function unlikePostComment(postId, commentId) {
  try {
    const headers = await getAuthHeader();
    const response = await axios.delete(
      `${API_BASE_URL}/api/posts/${postId}/comments/${commentId}/like/`,
      { headers }
    );
    return response.data;
  } catch (error) {
    console.error("Error unliking comment:", error);
    throw error;
  }
}

/**
 * Report a post
 */
export async function reportPost(postId, reason = "other", details = "") {
  try {
    const headers = await getAuthHeader();
    const response = await axios.post(
      `${API_BASE_URL}/api/posts/${postId}/report/`,
      { reason, details },
      { headers }
    );
    return response.data;
  } catch (error) {
    console.error("Error reporting post:", error);
    throw error;
  }
}

/**
 * Report a comment
 */
export async function reportPostComment(postId, commentId, reason = "other", details = "") {
  try {
    const headers = await getAuthHeader();
    const response = await axios.post(
      `${API_BASE_URL}/api/posts/${postId}/comments/${commentId}/report/`,
      { reason, details },
      { headers }
    );
    return response.data;
  } catch (error) {
    console.error("Error reporting comment:", error);
    throw error;
  }
}
